import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import type { TeamProject } from '@terum/contracts';

export type { TeamProject };

// D6 read path: the team/map graph payload carries no pinned/hidden/user_named flags, so the
// project kebab reads them here (to label Pin/Unpin, mark "renamed", and filter hidden projects).
export function useTeamProjects() {
  const query = useQuery({
    queryKey: ['surfaces', 'team-projects'],
    queryFn: () =>
      apiFetch<{ projects: TeamProject[]; unclustered_count?: number }>('/api/surfaces/team/projects'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  return {
    ...query,
    projects: query.data?.projects ?? [],
    unclusteredCount: query.data?.unclustered_count ?? 0,
  };
}

// Rename / Pin / Hide -> PATCH /api/surfaces/team/projects/:id (the Phase-1 route). Invalidates the
// projects list AND the graph + conversations caches so a hide/rename reflects in all three views.
export function useUpdateTeamProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: {
      id: string;
      updates: { name?: string; pinned?: boolean; hidden?: boolean };
    }) =>
      apiFetch(`/api/surfaces/team/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-projects'] });
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-map'] });
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-conversations'] });
    },
  });
}
